import React, { useState, useRef, useEffect, useMemo } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { uploadImageAndGetUrl, deleteImage, getSignedImageUrl, getBucketForCategory } from '../../../utils/imageUpload';
import { useAuth } from '../../../contexts/AuthContext';
import { worksService } from '../../../services/artistContentService';

const WorkSection = ({ artistData, onUpdate, isActive, artistType = 'Visual' }) => {
  const { user } = useAuth();
  const [workData, setWorkData] = useState([]);
  const [editingWork, setEditingWork] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [uploadingStates, setUploadingStates] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  
  // New state for work page configuration
  const [workPageConfig, setWorkPageConfig] = useState({
    workPageType: 'individual_works',
    workPageIcon: 'music',
    workPageTitle: 'work'
  });
  
  // Refs for maintaining focus on work title inputs
  const newWorkTitleRef = useRef(null);
  const editWorkTitleRefs = useRef({});
  const [activeInputId, setActiveInputId] = useState(null);

  // Form data for Add New Work - this will not trigger immediate updates
  const [formData, setFormData] = useState(() => {
    const base = {
      title: "",
      image: null,
      imagePath: null,
      workType: "" // Add work type field
    };

    if (artistType === 'Visual') {
      return {
        ...base,
        mediaType: "",
        dimensions: "",
        price: "",
        collectible: false,
        printSales: false,
        includeInShop: false,
        availabilityDate: "",
        removalDate: "",
        creationDate: ""
      };
    } else if (artistType === 'Model') {
      return {
        ...base,
        workDate: "",
        designer: "",
        photographer: "",
        url: "",
        videoLink: ""
      };
    } else if (artistType === 'Actor') {
      return {
        ...base,
        workDate: "",
        director: "",
        photographer: "",
        url: "",
        characterName: "",
        videoLink: ""
      };
    } else if (artistType === 'Music') {
      return {
        ...base,
        spotify: "",
        apple: "",
        tidal: "",
        youtube: ""
      };
    }
    
    return base;
  });

  // Form data for Edit Work - separate from display data  
  const [editFormData, setEditFormData] = useState(null);

  // Work type options as specified in user requirements
  const workTypeOptions = [
    { value: "Music", label: "Music" },
    { value: "Visual Art", label: "Visual Art" },
    { value: "Movie", label: "Movie" },
    { value: "Book", label: "Book" },
    { value: "Other", label: "Other" }
  ];

  // Work page type options as specified in user requirements
  const workPageTypeOptions = [
    { value: "streaming_platforms", label: "Artist Streaming Platforms" },
    { value: "individual_works", label: "Individual Works Below" }
  ];

  // Work page icon options with visual descriptions
  const workPageIconOptions = [
    { value: "music", label: "🎵 Music" },
    { value: "art", label: "🎨 Art" },
    { value: "film", label: "🎬 Film" },
    { value: "book", label: "📚 Book" },
    { value: "camera", label: "📷 Camera" }
  ];

  // Work page title options
  const workPageTitleOptions = [
    { value: "music", label: "Music" },
    { value: "art", label: "Art" },
    { value: "work", label: "Work" },
    { value: "films", label: "Films" },
    { value: "writings", label: "Writings" },
    { value: "photos", label: "Photos" }
  ];

  // Focus management effect
  useEffect(() => {
    if (activeInputId && typeof activeInputId === 'string') {
      const element = document.getElementById(activeInputId);
      if (element) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          element?.focus();
        }, 10);
      }
    }
  }, [formData, editFormData, activeInputId]);

  // Load works from database when component mounts or user changes
  useEffect(() => {
    const loadWorks = async () => {
      if (!user?.id) return;

      setLoading(true);
      setError(null);

      try {
        if (artistType === 'Music') {
          // For music artists, try to load existing music platform data
          const { data: worksData } = await worksService?.getWorks(user?.id);
          const musicWork = worksData?.find(work => work?.music_platforms);
          
          if (musicWork?.music_platforms) {
            setWorkData({
              spotify: musicWork?.music_platforms?.spotify || "",
              apple: musicWork?.music_platforms?.apple || "",
              tidal: musicWork?.music_platforms?.tidal || "",
              youtube: musicWork?.music_platforms?.youtube || ""
            });
          } else {
            setWorkData({
              spotify: "",
              apple: "",
              tidal: "",
              youtube: ""
            });
          }
        } else {
          // For other artist types, load works array
          const { data: worksData, error: worksError } = await worksService?.getWorks(user?.id);
          
          if (worksError) {
            setError('Failed to load works');
            return;
          }

          // Convert database works to component format and load signed URLs
          const convertedWorks = await Promise.all(
            (worksData || [])?.map(async (work) => {
              let convertedWork = {
                id: work?.id,
                title: work?.title,
                image: null,
                imagePath: work?.image_path
              };

              // Load signed URL for image if path exists
              if (work?.image_path) {
                const bucket = getBucketForCategory('work');
                const urlResult = await getSignedImageUrl(bucket, work?.image_path, 7200);
                if (urlResult?.data?.signedUrl) {
                  convertedWork.image = urlResult?.data?.signedUrl;
                }
              }

              // Add type-specific fields
              if (artistType === 'Visual') {
                convertedWork = {
                  ...convertedWork,
                  mediaType: work?.media_type || "",
                  dimensions: work?.dimensions || "",
                  price: work?.price || 0,
                  collectible: work?.collectible || false,
                  printSales: work?.print_sales || false,
                  includeInShop: work?.include_in_shop || false,
                  availabilityDate: work?.availability_date || "",
                  removalDate: work?.removal_date || "",
                  creationDate: work?.creation_date || ""
                };
              } else if (artistType === 'Model') {
                convertedWork = {
                  ...convertedWork,
                  workDate: work?.work_date || "",
                  designer: work?.designer || "",
                  photographer: work?.photographer || "",
                  url: work?.external_url || "",
                  videoLink: work?.video_link || ""
                };
              } else if (artistType === 'Actor') {
                convertedWork = {
                  ...convertedWork,
                  workDate: work?.work_date || "",
                  workType: work?.work_type || "",
                  director: work?.director || "",
                  photographer: work?.photographer || "",
                  url: work?.external_url || "",
                  characterName: work?.character_name || "",
                  videoLink: work?.video_link || ""
                };
              }

              return convertedWork;
            })
          );

          setWorkData(convertedWorks);
        }
      } catch (err) {
        console.error('Error loading works:', err);
        setError('Failed to load works');
      } finally {
        setLoading(false);
      }
    };

    loadWorks();
  }, [user?.id, artistType]);

  // Also update when artistData.work changes (from parent)
  useEffect(() => {
    if (artistData?.work && !loading) {
      if (artistType === 'Music') {
        if (typeof artistData?.work === 'object' && !Array.isArray(artistData?.work)) {
          setWorkData(artistData?.work);
        }
      } else if (Array.isArray(artistData?.work)) {
        setWorkData(artistData?.work);
      }
    }
  }, [artistData?.work, loading, artistType]);

  // Load work page configuration when component mounts
  useEffect(() => {
    const loadWorkPageConfig = async () => {
      if (!user?.id) return;

      try {
        // Use the getUserProfile method from worksService
        const userProfile = await worksService?.getUserProfile?.(user?.id);
        if (userProfile) {
          setWorkPageConfig({
            workPageType: userProfile?.work_page_type || 'individual_works',
            workPageIcon: userProfile?.work_page_icon || 'music',
            workPageTitle: userProfile?.work_page_title || 'work'
          });
        }
      } catch (error) {
        console.error('Error loading work page config:', error);
      }
    };

    loadWorkPageConfig();
  }, [user?.id]);

  const handleDragStart = (e, index) => {
    if (artistType === 'Music') return;
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e?.preventDefault();
    
    if (draggedItem === null || artistType === 'Music') return;
    
    const newWorks = [...workData];
    const draggedWork = newWorks?.[draggedItem];
    
    newWorks?.splice(draggedItem, 1);
    newWorks?.splice(dropIndex, 0, draggedWork);
    
    // Update sort_order for reordering
    const updatedWorks = newWorks?.map((work, index) => ({
      ...work,
      sortOrder: index
    }));
    
    setWorkData(updatedWorks);
    setDraggedItem(null);

    // Save new order to database
    try {
      const orderUpdates = updatedWorks?.map((work, index) => ({
        id: work?.id,
        sort_order: index
      }));
      
      await worksService?.reorderWorks(orderUpdates);
    } catch (error) {
      console.error('Error reordering works:', error);
      setError('Failed to save work order');
    }
  };

  const handleImageUpload = async (file, isNew = false, workId = null) => {
    if (!file || !file?.type?.startsWith('image/') || !user?.id) {
      return;
    }

    const uploadKey = isNew ? 'newWork' : workId || 'editing';
    setUploadingStates(prev => ({ ...prev, [uploadKey]: true }));

    try {
      // Delete old image if it exists
      let oldImagePath = null;
      if (isNew && formData?.imagePath) {
        oldImagePath = formData?.imagePath;
      } else if (editFormData?.imagePath) {
        oldImagePath = editFormData?.imagePath;
      }

      if (oldImagePath) {
        const bucket = getBucketForCategory('work');
        await deleteImage(bucket, oldImagePath);
      }

      // Upload new image
      const result = await uploadImageAndGetUrl(file, user?.id, 'work');
      
      if (result?.error) {
        console.error('Upload error:', result?.error);
        setError('Failed to upload image');
        return;
      }

      // Update the appropriate form state
      if (isNew) {
        setFormData(prev => ({ 
          ...prev, 
          image: result?.data?.url,
          imagePath: result?.data?.path
        }));
      } else if (editFormData) {
        setEditFormData(prev => ({ 
          ...prev, 
          image: result?.data?.url,
          imagePath: result?.data?.path
        }));
      }

    } catch (error) {
      console.error('Image upload error:', error);
      setError('Failed to upload image');
    } finally {
      setUploadingStates(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const startEdit = (work) => {
    setEditingWork({ ...work });
    setEditFormData({ ...work });
    setShowAddForm(false);
  };

  const saveEdit = async () => {
    if (!editFormData || !editingWork) return;

    try {
      setError(null);

      if (artistType === 'Music') {
        setWorkData(editingWork);
      } else {
        // Prepare data for database update
        const updateData = {
          title: editFormData?.title,
          image_path: editFormData?.imagePath,
          work_type: editFormData?.workType || artistType?.toLowerCase(), // FIXED: Save selected work type
          sort_order: editingWork?.sortOrder || 0
        };

        // Add type-specific fields
        if (artistType === 'Visual') {
          updateData.media_type = editFormData?.mediaType;
          updateData.dimensions = editFormData?.dimensions;
          updateData.price = parseFloat(editFormData?.price) || null;
          updateData.collectible = editFormData?.collectible;
          updateData.print_sales = editFormData?.printSales;
          updateData.include_in_shop = editFormData?.includeInShop;
          updateData.availability_date = editFormData?.availabilityDate || null;
          updateData.removal_date = editFormData?.removalDate || null;
          updateData.creation_date = editFormData?.creationDate || null;
        } else if (artistType === 'Model') {
          updateData.work_date = editFormData?.workDate || null;
          updateData.designer = editFormData?.designer;
          updateData.photographer = editFormData?.photographer;
          updateData.external_url = editFormData?.url;
          updateData.video_link = editFormData?.videoLink;
        } else if (artistType === 'Actor') {
          updateData.work_date = editFormData?.workDate || null;
          updateData.work_type = editFormData?.workType;
          updateData.director = editFormData?.director;
          updateData.photographer = editFormData?.photographer;
          updateData.external_url = editFormData?.url;
          updateData.character_name = editFormData?.characterName;
          updateData.video_link = editFormData?.videoLink;
        }

        // Update in database
        const { error: updateError } = await worksService?.updateWork(editingWork?.id, updateData);
        
        if (updateError) {
          setError('Failed to update work');
          return;
        }

        // Refresh image URL if path exists
        let workToSave = { ...editFormData };
        if (editFormData?.imagePath) {
          const bucket = getBucketForCategory('work');
          const urlResult = await getSignedImageUrl(bucket, editFormData?.imagePath, 7200);
          if (urlResult?.data) {
            workToSave.image = urlResult?.data?.signedUrl;
          }
        }
        
        setWorkData(prev => prev?.map(work => 
          work?.id === editingWork?.id ? workToSave : work
        ));
      }

      setEditingWork(null);
      setEditFormData(null);
      setActiveInputId(null);
    } catch (error) {
      console.error('Error saving work edit:', error);
      setError('Failed to save changes');
    }
  };

  const cancelEdit = () => {
    setEditingWork(null);
    setEditFormData(null);
    setActiveInputId(null);
  };

  const deleteWork = async (id) => {
    try {
      setError(null);

      // Find work to delete and remove its image from storage
      const workToDelete = workData?.find(work => work?.id === id);
      if (workToDelete?.imagePath) {
        const bucket = getBucketForCategory('work');
        await deleteImage(bucket, workToDelete?.imagePath);
      }

      // Delete from database
      const { error: deleteError } = await worksService?.deleteWork(id);
      
      if (deleteError) {
        setError('Failed to delete work');
        return;
      }

      // Remove from local state
      setWorkData(prev => prev?.filter(work => work?.id !== id));
    } catch (error) {
      console.error('Error deleting work:', error);
      setError('Failed to delete work');
    }
  };

  const addNewWork = async () => {
    if (!formData?.title) return;

    try {
      setError(null);

      // Prepare data for database creation
      const createData = {
        title: formData?.title,
        image_path: formData?.imagePath,
        work_type: formData?.workType || 'Other', // FIXED: Use selected work type from dropdown
        sort_order: workData?.length
      };

      // For Music work type, save music platform URLs
      if (formData?.workType === 'Music') {
        createData.music_platforms = {
          spotify: formData?.spotify || "",
          apple: formData?.apple || "",
          tidal: formData?.tidal || "",
          youtube: formData?.youtube || ""
        };
      } else {
        // For other work types, save the optional URL
        createData.external_url = formData?.url || "";
      }

      // Add type-specific fields for backward compatibility
      if (artistType === 'Visual') {
        createData.media_type = formData?.mediaType;
        createData.dimensions = formData?.dimensions;
        createData.price = parseFloat(formData?.price) || null;
        createData.collectible = formData?.collectible;
        createData.print_sales = formData?.printSales;
        createData.include_in_shop = formData?.includeInShop;
        createData.availability_date = formData?.availabilityDate || null;
        createData.removal_date = formData?.removalDate || null;
        createData.creation_date = formData?.creationDate || null;
      } else if (artistType === 'Model') {
        createData.work_date = formData?.workDate || null;
        createData.designer = formData?.designer;
        createData.photographer = formData?.photographer;
        createData.video_link = formData?.videoLink;
      } else if (artistType === 'Actor') {
        createData.work_date = formData?.workDate || null;
        createData.director = formData?.director;
        createData.photographer = formData?.photographer;
        createData.character_name = formData?.characterName;
        createData.video_link = formData?.videoLink;
      }

      // Create in database
      const { data: newWork, error: createError } = await worksService?.createWork(user?.id, createData);
      
      if (createError) {
        setError('Failed to create work');
        return;
      }

      // Prepare work for local state
      let workToAdd = { ...formData };
      workToAdd.id = newWork?.id;

      // Refresh image URL if path exists
      if (formData?.imagePath) {
        const bucket = getBucketForCategory('work');
        const urlResult = await getSignedImageUrl(bucket, formData?.imagePath, 7200);
        if (urlResult?.data) {
          workToAdd.image = urlResult?.data?.signedUrl;
        }
      }

      setWorkData(prev => [...prev, workToAdd]);
      
      // Reset form data
      setFormData(() => {
        const base = {
          title: "",
          image: null,
          imagePath: null,
          workType: ""
        };

        if (artistType === 'Visual') {
          return {
            ...base,
            mediaType: "",
            dimensions: "",
            price: "",
            collectible: false,
            printSales: false,
            includeInShop: false,
            availabilityDate: "",
            removalDate: "",
            creationDate: ""
          };
        } else if (artistType === 'Model') {
          return {
            ...base,
            workDate: "",
            designer: "",
            photographer: "",
            url: "",
            videoLink: ""
          };
        } else if (artistType === 'Actor') {
          return {
            ...base,
            workDate: "",
            workType: "",
            director: "",
            photographer: "",
            url: "",
            characterName: "",
            videoLink: ""
          };
        } else if (artistType === 'Music') {
          return {
            ...base,
            spotify: "",
            apple: "",
            tidal: "",
            youtube: ""
          };
        }
        
        return base;
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding work:', error);
      setError('Failed to add work');
    }
  };

  // Handle work page configuration changes
  const handleWorkPageConfigChange = async (field, value) => {
    const newConfig = { ...workPageConfig, [field]: value };
    setWorkPageConfig(newConfig);

    try {
      // Update in database
      const updateParams = {};
      updateParams[field === 'workPageType' ? 'page_type' : field === 'workPageIcon' ? 'page_icon' : 'page_title'] = value;
      
      const result = await worksService?.updateWorkPageConfig?.(user?.id, updateParams);
      if (result?.error) {
        setError('Failed to save work page configuration');
        // Revert on error
        setWorkPageConfig(workPageConfig);
      }
    } catch (error) {
      console.error('Error updating work page config:', error);
      setError('Failed to save work page configuration');
      setWorkPageConfig(workPageConfig); // Revert on error
    }
  };

  const handleSave = async () => {
    // For music artists, the data is already saved in real-time
    // For others, data is saved when individual operations happen
    // This just updates the parent component state for consistency
    onUpdate?.('work', workData);
  };

  // Music Artist Platform Links
  if (artistType === 'Music') {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-lg">
            {error}
          </div>
        )}
        
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success rounded-lg flex items-center justify-center">
              <Icon name="Music" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Music Platforms</h2>
              <p className="text-sm text-text-secondary">Add links to your music on streaming platforms</p>
            </div>
          </div>
          
          <Button variant="default" size="sm" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Spotify"
            type="url"
            placeholder="https://open.spotify.com/artist/..."
            value={editingWork ? editingWork?.spotify : workData?.spotify || ""}
            onChange={(e) => {
              if (editingWork) {
                setEditingWork(prev => ({ ...prev, spotify: e?.target?.value }));
              } else {
                setWorkData(prev => ({ ...prev, spotify: e?.target?.value }));
              }
            }}
          />
          
          <Input
            label="Apple Music"
            type="url"
            placeholder="https://music.apple.com/artist/..."
            value={editingWork ? editingWork?.apple : workData?.apple || ""}
            onChange={(e) => {
              if (editingWork) {
                setEditingWork(prev => ({ ...prev, apple: e?.target?.value }));
              } else {
                setWorkData(prev => ({ ...prev, apple: e?.target?.value }));
              }
            }}
          />
          
          <Input
            label="Tidal"
            type="url"
            placeholder="https://tidal.com/browse/artist/..."
            value={editingWork ? editingWork?.tidal : workData?.tidal || ""}
            onChange={(e) => {
              if (editingWork) {
                setEditingWork(prev => ({ ...prev, tidal: e?.target?.value }));
              } else {
                setWorkData(prev => ({ ...prev, tidal: e?.target?.value }));
              }
            }}
          />
          
          <Input
            label="YouTube Music"
            type="url"
            placeholder="https://music.youtube.com/channel/..."
            value={editingWork ? editingWork?.youtube : workData?.youtube || ""}
            onChange={(e) => {
              if (editingWork) {
                setEditingWork(prev => ({ ...prev, youtube: e?.target?.value }));
              } else {
                setWorkData(prev => ({ ...prev, youtube: e?.target?.value }));
              }
            }}
          />
        </div>
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon 
              name={Object.values(workData || {})?.some(url => url) ? "CheckCircle" : "AlertCircle"} 
              size={16} 
              className={Object.values(workData || {})?.some(url => url) ? "text-success" : "text-warning"}
            />
            <span className="text-sm font-medium text-text-primary">
              Platform Status: {Object.values(workData || {})?.filter(url => url)?.length} platforms linked
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Visual/Model/Actor Work Management
  const WorkForm = ({ work, onChange, onImageUpload, isNew = false, workId = null }) => {
    const uploadKey = isNew ? 'newWork' : workId || 'editing';
    const isUploading = uploadingStates?.[uploadKey];
    
    // Stable key for Work Title input to prevent re-mounting
    const workTitleInputKey = useMemo(() => 
      isNew ? 'new-work-title' : `edit-work-title-${workId}`,
      [isNew, workId]
    );
    
    // Handle title change with focus management
    const handleTitleChange = (e) => {
      const inputId = e?.target?.id;
      setActiveInputId(inputId);
      onChange({ ...work, title: e?.target?.value });
    };

    // Handle focus events
    const handleTitleFocus = (e) => {
      setActiveInputId(e?.target?.id);
    };

    const handleTitleBlur = () => {
      // Small delay to prevent losing focus during rapid updates
      setTimeout(() => {
        setActiveInputId(null);
      }, 100);
    };
    
    return (
      <div className="space-y-4 p-4 border border-border rounded-lg bg-muted/50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            key={workTitleInputKey}
            id={workTitleInputKey}
            ref={isNew ? newWorkTitleRef : (ref) => {
              if (ref && workId) {
                editWorkTitleRefs.current[workId] = ref;
              }
            }}
            label="Work Title"
            type="text"
            placeholder="Enter work title"
            value={work?.title || ""}
            onChange={handleTitleChange}
            onFocus={handleTitleFocus}
            onBlur={handleTitleBlur}
            required
          />
          
          {/* Work Type Dropdown - Always shown for all artist types */}
          <Select
            label="Work Type"
            options={workTypeOptions}
            value={work?.workType || ""}
            onChange={(value) => onChange({ ...work, workType: value })}
            placeholder="Select work type"
            required
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-text-primary">Work Image</label>
          <div className="space-y-4">
            {work?.image && (
              <div className="relative inline-block">
                <Image
                  src={work?.image}
                  alt="Work preview"
                  className="w-20 h-20 object-cover rounded-lg"
                />
                {!isUploading && (
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 w-6 h-6"
                    onClick={() => onChange({ ...work, image: null, imagePath: null })}
                  >
                    <Icon name="X" size={12} />
                  </Button>
                )}
              </div>
            )}
            
            <div className="flex items-center space-x-4">
              {isUploading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-text-secondary">Uploading...</span>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef?.current?.click()}
                  iconName="Upload"
                  iconPosition="left"
                  iconSize={16}
                >
                  {work?.image ? 'Change Image' : 'Upload Image'}
                </Button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={(e) => onImageUpload(e?.target?.files?.[0], isNew, workId)}
                className="hidden"
                disabled={isUploading}
              />
            </div>
          </div>
        </div>

        {/* Music Work Type - Show music platform inputs */}
        {work?.workType === 'Music' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Spotify URL"
              type="url"
              placeholder="https://open.spotify.com/..."
              value={work?.spotify || ""}
              onChange={(e) => onChange({ ...work, spotify: e?.target?.value })}
            />
            
            <Input
              label="Apple Music URL"
              type="url"
              placeholder="https://music.apple.com/..."
              value={work?.apple || ""}
              onChange={(e) => onChange({ ...work, apple: e?.target?.value })}
            />
            
            <Input
              label="YouTube Music URL"
              type="url"
              placeholder="https://music.youtube.com/..."
              value={work?.youtube || ""}
              onChange={(e) => onChange({ ...work, youtube: e?.target?.value })}
            />
            
            <Input
              label="Tidal URL"
              type="url"
              placeholder="https://tidal.com/..."
              value={work?.tidal || ""}
              onChange={(e) => onChange({ ...work, tidal: e?.target?.value })}
            />
          </div>
        )}

        {/* All other work types - Show optional URL box */}
        {work?.workType && work?.workType !== 'Music' && (
          <Input
            label="URL (Optional)"
            type="url"
            placeholder="https://example.com"
            value={work?.url || ""}
            onChange={(e) => onChange({ ...work, url: e?.target?.value })}
          />
        )}

        {/* Keep existing type-specific fields for backward compatibility */}
        {artistType === 'Visual' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Media Type"
                options={[
                  { value: "Oil on Canvas", label: "Oil on Canvas" },
                  { value: "Acrylic", label: "Acrylic" },
                  { value: "Watercolor", label: "Watercolor" },
                  { value: "Mixed Media", label: "Mixed Media" },
                  { value: "Digital Art", label: "Digital Art" },
                  { value: "Photography", label: "Photography" },
                  { value: "Sculpture", label: "Sculpture" }
                ]}
                value={work?.mediaType || ""}
                onChange={(value) => onChange({ ...work, mediaType: value })}
                placeholder="Select media type"
              />
              
              <Input
                label="Dimensions"
                type="text"
                placeholder='e.g., 24" x 36"'
                value={work?.dimensions || ""}
                onChange={(e) => onChange({ ...work, dimensions: e?.target?.value })}
              />
              
              <Input
                label="Price ($)"
                type="number"
                placeholder="0"
                value={work?.price || ""}
                onChange={(e) => onChange({ ...work, price: e?.target?.value })}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-text-primary">Options</label>
              <div className="flex flex-wrap gap-4">
                <Checkbox
                  label="Collectible Item"
                  checked={work?.collectible || false}
                  onChange={(e) => onChange({ ...work, collectible: e?.target?.checked })}
                />
                <Checkbox
                  label="Print Sales Available"
                  checked={work?.printSales || false}
                  onChange={(e) => onChange({ ...work, printSales: e?.target?.checked })}
                />
                <Checkbox
                  label="Include in Shop"
                  checked={work?.includeInShop || false}
                  onChange={(e) => onChange({ ...work, includeInShop: e?.target?.checked })}
                />
              </div>
            </div>
          </>
        )}

        {/* Keep existing fields for other artist types */}
        {(artistType === 'Model' || artistType === 'Actor') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={artistType === 'Model' ? "Designer" : "Director"}
              type="text"
              placeholder={artistType === 'Model' ? "Enter designer name" : "Enter director name"}
              value={artistType === 'Model' ? (work?.designer || "") : (work?.director || "")}
              onChange={(e) => onChange({ 
                ...work, 
                [artistType === 'Model' ? 'designer' : 'director']: e?.target?.value 
              })}
            />
            
            <Input
              label="Photographer"
              type="text"
              placeholder="Enter photographer name"
              value={work?.photographer || ""}
              onChange={(e) => onChange({ ...work, photographer: e?.target?.value })}
            />
            
            {artistType === 'Actor' && (
              <Input
                label="Character Name"
                type="text"
                placeholder="Enter character name"
                value={work?.characterName || ""}
                onChange={(e) => onChange({ ...work, characterName: e?.target?.value })}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  if (!isActive) return null;

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-text-secondary">Loading works...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-lg">
          {error}
        </div>
      )}
      
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-success rounded-lg flex items-center justify-center">
            <Icon name="Briefcase" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Work Portfolio</h2>
            <p className="text-sm text-text-secondary">Configure your work page and add works by type</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowAddForm(true)}
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
            disabled={loading}
          >
            Add New Work
          </Button>
          <Button variant="default" size="sm" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* NEW: Work Page Configuration Section */}
      <div className="mb-8 p-6 bg-muted/30 border border-muted rounded-lg">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Settings" size={20} className="mr-2" />
          Work Page Configuration
        </h3>
        <p className="text-sm text-text-secondary mb-6">
          Configure how your work section appears in the mobile app
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Select
              label="Select what to display on the Work Page:"
              options={workPageTypeOptions}
              value={workPageConfig?.workPageType || "individual_works"}
              onChange={(value) => handleWorkPageConfigChange('workPageType', value)}
              placeholder="Select display type"
            />
            <p className="text-xs text-text-secondary mt-1">
              Choose between streaming platform links or individual work items
            </p>
          </div>

          <div>
            <Select
              label="Select Work Page Icon:"
              options={workPageIconOptions}
              value={workPageConfig?.workPageIcon || "music"}
              onChange={(value) => handleWorkPageConfigChange('workPageIcon', value)}
              placeholder="Select icon"
            />
            <p className="text-xs text-text-secondary mt-1">
              Icon shown in the bottom menu of the mobile app
            </p>
          </div>

          <div>
            <Select
              label="Select Work Page Title:"
              options={workPageTitleOptions}
              value={workPageConfig?.workPageTitle || "work"}
              onChange={(value) => handleWorkPageConfigChange('workPageTitle', value)}
              placeholder="Select title"
            />
            <p className="text-xs text-text-secondary mt-1">
              Title displayed on the work page in the mobile app
            </p>
          </div>
        </div>

        {/* Configuration Preview */}
        <div className="mt-6 p-4 bg-surface border rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Smartphone" size={16} className="text-accent" />
            <span className="text-sm font-medium text-text-primary">Mobile App Preview:</span>
          </div>
          <div className="text-sm text-text-secondary">
            <p>
              <strong>Display Type:</strong> {workPageTypeOptions?.find(opt => opt?.value === workPageConfig?.workPageType)?.label}
            </p>
            <p>
              <strong>Bottom Menu Icon:</strong> {workPageIconOptions?.find(opt => opt?.value === workPageConfig?.workPageIcon)?.label}
            </p>
            <p>
              <strong>Page Title:</strong> {workPageTitleOptions?.find(opt => opt?.value === workPageConfig?.workPageTitle)?.label}
            </p>
          </div>
        </div>
      </div>
      
      {/* Add New Work Form */}
      {showAddForm && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-text-primary">Add New Work</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>
              <Icon name="X" size={16} />
            </Button>
          </div>
          
          <WorkForm
            work={formData}
            onChange={setFormData}
            onImageUpload={handleImageUpload}
            isNew={true}
          />
          
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={addNewWork}
              disabled={uploadingStates?.newWork || loading}
            >
              Add Work
            </Button>
          </div>
        </div>
      )}
      
      {/* Works List */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2 text-sm text-text-secondary">
          <Icon name="Move" size={16} />
          <span>Drag works to reorder them</span>
        </div>

        {workData?.length > 0 ? (
          workData?.map((work, index) => (
            <div key={work?.id}>
              {editingWork && editingWork?.id === work?.id ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-text-primary">Edit Work</h3>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={cancelEdit}>
                        Cancel
                      </Button>
                      <Button 
                        variant="default" 
                        size="sm" 
                        onClick={saveEdit}
                        disabled={Object.values(uploadingStates)?.some(Boolean) || loading}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                  
                  <WorkForm
                    work={editFormData}
                    onChange={setEditFormData}
                    onImageUpload={handleImageUpload}
                    workId={editingWork?.id}
                  />
                </div>
              ) : (
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  className="flex items-center space-x-4 p-4 border border-border rounded-lg hover:bg-muted/50 cursor-move transition-colors"
                >
                  <Icon name="GripVertical" size={20} className="text-text-secondary" />
                  
                  <Image
                    src={work?.image || "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400"}
                    alt={work?.title}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  
                  <div className="flex-1">
                    <h4 className="font-medium text-text-primary">{work?.title}</h4>
                    <div className="text-sm text-text-secondary">
                      <p className="font-medium text-accent">{work?.workType || 'Unspecified'}</p>
                      {work?.workType === 'Music' && work?.spotify && (
                        <p className="text-xs">Music platforms available</p>
                      )}
                      {work?.workType && work?.workType !== 'Music' && work?.url && (
                        <p className="text-xs">External link available</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => startEdit(work)}
                      disabled={loading}
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteWork(work?.id)}
                      className="text-destructive hover:text-destructive"
                      disabled={loading}
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon name="Briefcase" size={48} className="mx-auto text-text-secondary mb-4" />
            <p className="text-text-secondary">No works added yet</p>
            <p className="text-sm text-text-secondary mt-1">
              Click "Add New Work" and select from Music, Visual Art, Movie, Book, or Other
            </p>
          </div>
        )}
      </div>
      
      {/* Status Indicator */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon 
            name={workData?.length > 0 ? "CheckCircle" : "AlertCircle"} 
            size={16} 
            className={workData?.length > 0 ? "text-success" : "text-warning"}
          />
          <span className="text-sm font-medium text-text-primary">
            Work Status: {workData?.length > 0 ? `${workData?.length} works added` : "No works"}
          </span>
        </div>
        {workData?.length === 0 && (
          <p className="text-sm text-text-secondary mt-1">
            Add your works with the new work type system: Music, Visual Art, Movie, Book, or Other.
          </p>
        )}
      </div>
    </div>
  );
};

export default WorkSection;